import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ARCHETYPES, IMMERSION_PHASES, PHASE_LABELS } from './archetypeData';

const ImmersionExperience = ({ archetypeId, musicTrack, onComplete }) => {
  const archetype = ARCHETYPES[archetypeId];
  const [currentPhaseIndex, setCurrentPhaseIndex] = useState(0);
  const [displayText, setDisplayText] = useState('');
  const [isTransitioning, setIsTransitioning] = useState(false);
  const audioRef = useRef(null);
  const currentPhase = IMMERSION_PHASES[currentPhaseIndex];

  // Phase timings (in ms)
  const PHASE_DURATIONS = {
    breath: 20000,
    identity: 25000,
    embodiment: 18000,
    activation: 15000,
    anchor: 12000
  };

  // Volume targets for each phase (builds from very quiet to fuller)
  const PHASE_VOLUMES = {
    breath: 0.08,      // Start very quiet
    identity: 0.18,    // Building
    embodiment: 0.28,  // More present
    activation: 0.38,  // Rising
    anchor: 0.48       // Full presence
  };

  useEffect(() => {
    // Start music VERY quietly
    if (audioRef.current && musicTrack) {
      audioRef.current.src = musicTrack.url;
      audioRef.current.volume = 0.02; // Start nearly silent
      audioRef.current.loop = true;
      audioRef.current.play().catch(() => {});

      return () => {
        if (audioRef.current) {
          audioRef.current.pause();
        }
      };
    }
  }, [musicTrack]);

  // Music builds through each stage - smooth ramping
  useEffect(() => {
    if (!audioRef.current) return;
    
    const targetVolume = PHASE_VOLUMES[currentPhase] || 0.2;
    const startVolume = audioRef.current.volume;
    const volumeDiff = targetVolume - startVolume;
    const totalSteps = 30; // 3 seconds of ramping
    let currentStep = 0;
    
    const volumeInterval = setInterval(() => {
      if (audioRef.current && currentStep < totalSteps) {
        const progress = currentStep / totalSteps;
        // Ease-in-out for smooth volume transition
        const easedProgress = progress < 0.5 
          ? 2 * progress * progress 
          : 1 - Math.pow(-2 * progress + 2, 2) / 2;
        
        audioRef.current.volume = Math.max(0.02, Math.min(0.6, startVolume + (volumeDiff * easedProgress)));
        currentStep++;
      } else {
        clearInterval(volumeInterval);
      }
    }, 100);

    return () => clearInterval(volumeInterval);
  }, [currentPhase]);

  useEffect(() => {
    // Set current phase text
    const script = archetype?.immersionScript?.[currentPhase] || '';
    setDisplayText(script);
  }, [currentPhase, archetype]);

  useEffect(() => {
    // Auto-advance phases
    if (currentPhaseIndex < IMMERSION_PHASES.length) {
      const timer = setTimeout(() => {
        if (currentPhaseIndex < IMMERSION_PHASES.length - 1) {
          setIsTransitioning(true);
          setTimeout(() => {
            setCurrentPhaseIndex(prev => prev + 1);
            setIsTransitioning(false);
          }, 800);
        } else {
          // Complete immersion - don't stop music, let it continue into next screen
          setTimeout(() => {
            // Just complete - music will be managed by parent TuneInToReality
            onComplete(audioRef.current); // Pass audio ref so it can continue
          }, 2000);
        }
      }, PHASE_DURATIONS[currentPhase]);

      return () => clearTimeout(timer);
    }
  }, [currentPhaseIndex, currentPhase, onComplete]);

  return (
    <div className="fixed inset-0 bg-[#050508] z-50 overflow-hidden">
      <audio ref={audioRef} />
      
      {/* Ambient particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(30)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 rounded-full bg-white/20"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`
            }}
            animate={{
              y: [-20, 20, -20],
              x: [-10, 10, -10],
              opacity: [0.1, 0.3, 0.1]
            }}
            transition={{
              duration: 8 + Math.random() * 4,
              repeat: Infinity,
              delay: Math.random() * 5
            }}
          />
        ))}
      </div>

      {/* Central glow based on archetype */}
      <motion.div
        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] rounded-full pointer-events-none"
        style={{
          background: `radial-gradient(circle, ${archetype?.glowColor || 'rgba(147, 51, 234, 0.2)'} 0%, transparent 70%)`
        }}
        animate={{
          scale: [1, 1.1, 1],
          opacity: [0.3, 0.5, 0.3]
        }}
        transition={{
          duration: 6,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      />

      {/* Phase indicators - at top */}
      <div className="absolute top-8 left-1/2 -translate-x-1/2 flex gap-2">
        {IMMERSION_PHASES.map((phase, index) => (
          <motion.div
            key={phase}
            className={`w-2 h-2 rounded-full transition-colors duration-500 ${
              index === currentPhaseIndex
                ? 'bg-white/60'
                : index < currentPhaseIndex
                ? 'bg-white/30'
                : 'bg-white/10'
            }`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: index * 0.2 }}
          />
        ))}
      </div>

      {/* Phase label - PROMINENT */}
      <motion.div
        key={currentPhase}
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="absolute top-16 left-1/2 -translate-x-1/2 text-center"
      >
        <p 
          className="text-lg font-medium uppercase tracking-[0.4em]"
          style={{ color: archetype?.color || '#9333EA' }}
        >
          {PHASE_LABELS[currentPhase]}
        </p>
        <div 
          className="w-12 h-0.5 mx-auto mt-2 rounded-full"
          style={{ backgroundColor: archetype?.color || '#9333EA', opacity: 0.5 }}
        />
      </motion.div>

      {/* Main content - centered text */}
      <div className="absolute inset-0 flex items-center justify-center px-10">
        <AnimatePresence mode="wait">
          {!isTransitioning && (
            <motion.div
              key={currentPhase}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.8 }}
              className="text-center max-w-md"
            >
              {displayText.split('\n\n').map((paragraph, pIndex) => (
                <motion.p
                  key={pIndex}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: pIndex * 1.5 }}
                  className="text-white/80 text-lg leading-relaxed mb-6 font-light"
                  style={{ whiteSpace: 'pre-line' }}
                >
                  {paragraph}
                </motion.p>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Anchor moment special effect */}
      {currentPhase === 'anchor' && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 3 }}
          className="absolute bottom-20 left-1/2 -translate-x-1/2"
        >
          <motion.div
            animate={{ scale: [1, 1.1, 1] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="w-16 h-16 rounded-full border-2 border-white/30 flex items-center justify-center"
          >
            <div 
              className="w-8 h-8 rounded-full"
              style={{ backgroundColor: archetype?.color }}
            />
          </motion.div>
        </motion.div>
      )}
    </div>
  );
};

export default ImmersionExperience;
